import { isGoogleFontLoaded, loadGoogleFontWeights } from "@/lib/GoogleFontLoader";

/**
 * Match `load_font("Family")` / `load_font('Family')` string literals so the host
 * can preload Google Fonts before the sync Luau VM runs.
 */
const LOAD_FONT_CALL_RE = /\bload_font\s*\(\s*(["'])((?:\\.|(?!\1).)*)\1\s*\)/g;

/** Unique family names from `load_font("…")` literals in script source. */
export function extractLoadFontFamilies(source: string): string[] {
  const families: string[] = [];
  const seen = new Set<string>();
  LOAD_FONT_CALL_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = LOAD_FONT_CALL_RE.exec(source)) !== null) {
    const family = unescapeLuauString(match[2] ?? "").trim();
    if (!family || seen.has(family)) {
      continue;
    }
    seen.add(family);
    families.push(family);
  }
  return families;
}

/**
 * Await Google Font CSS + face load for every `load_font("…")` literal.
 * Safe to call when there are none (no-op).
 */
export async function preloadScriptFonts(source: string): Promise<string[]> {
  const families = extractLoadFontFamilies(source);
  if (families.length === 0) {
    return families;
  }
  await Promise.all(families.map(family => loadGoogleFontWeights(family)));
  return families;
}

/** Ensure a family is loading; return whether it is already usable. */
export function ensureScriptFontReady(family: string): boolean {
  const trimmed = family.trim();
  if (!trimmed) {
    return false;
  }
  if (isGoogleFontLoaded(trimmed)) {
    return true;
  }
  void loadGoogleFontWeights(trimmed);
  return false;
}

function unescapeLuauString(value: string): string {
  return value
    .replace(/\\'/g, "'")
    .replace(/\\"/g, "\"")
    .replace(/\\\\/g, "\\")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t");
}
